import { useCurrentUser } from '@/hooks/use-auth';
import { del, get, post, put } from '@/lib/api';
import {
	POLICY_ACTION_COLORS,
	POLICY_ACTION_LABELS,
	THREAT_CLASS_LABELS,
	formatTimestamp,
} from '@/lib/constants';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PolicyExceptionSummary, PolicyRuleSummary } from '@uncain/shared';
import { useState } from 'react';

export function PoliciesPage() {
	const { data: user } = useCurrentUser();
	const canMutate = user?.currentOrg?.role === 'operator' || user?.currentOrg?.role === 'admin';
	const [showNewForm, setShowNewForm] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);

	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-foreground">Policies</h1>
				{canMutate && (
					<button
						type="button"
						onClick={() => setShowNewForm(!showNewForm)}
						className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
					>
						{showNewForm ? 'Cancel' : 'New Policy'}
					</button>
				)}
			</div>

			{showNewForm && <PolicyForm onClose={() => setShowNewForm(false)} />}

			<PolicyList canMutate={canMutate} editingId={editingId} setEditingId={setEditingId} />

			<div className="border-t border-border pt-8">
				<h2 className="text-xl font-bold text-foreground">Exceptions</h2>
				<ExceptionList canMutate={canMutate} />
			</div>
		</div>
	);
}

function PolicyList({
	canMutate,
	editingId,
	setEditingId,
}: {
	canMutate: boolean;
	editingId: string | null;
	setEditingId: (id: string | null) => void;
}) {
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery<{ policies: PolicyRuleSummary[] }>({
		queryKey: queryKeys.policies.list(),
		queryFn: () => get<{ policies: PolicyRuleSummary[] }>('/policies'),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => del(`/policies/${id}`),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.policies.all }),
	});

	if (isLoading) {
		return <div className="text-muted-foreground">Loading policies...</div>;
	}

	const policies = data?.policies ?? [];

	if (policies.length === 0) {
		return (
			<div className="rounded-lg border border-border bg-card p-8 text-center">
				<p className="text-muted-foreground">No policies configured.</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{policies.map((policy) => (
				<div key={policy.id} className="rounded-lg border border-border bg-card p-4">
					{editingId === policy.id ? (
						<PolicyForm existing={policy} onClose={() => setEditingId(null)} />
					) : (
						<div className="flex items-center justify-between">
							<div className="space-y-1">
								<div className="flex items-center gap-2">
									<span className="text-sm font-medium text-foreground">{policy.name}</span>
									<span
										className={cn(
											'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
											POLICY_ACTION_COLORS[policy.action] ?? 'bg-gray-100 text-gray-800',
										)}
									>
										{POLICY_ACTION_LABELS[policy.action] ?? policy.action}
									</span>
									{!policy.enabled && (
										<span className="text-xs text-muted-foreground">(disabled)</span>
									)}
								</div>
								{policy.description && (
									<p className="text-xs text-muted-foreground">{policy.description}</p>
								)}
								<p className="text-xs text-muted-foreground">
									{THREAT_CLASS_LABELS[policy.conditionType] ?? policy.conditionType}
									{' · '}Scope: {policy.scopeType}
								</p>
							</div>
							{canMutate && (
								<div className="flex gap-2">
									<button
										type="button"
										onClick={() => setEditingId(policy.id)}
										className="text-xs text-primary hover:underline"
									>
										Edit
									</button>
									<button
										type="button"
										onClick={() => deleteMutation.mutate(policy.id)}
										className="text-xs text-destructive hover:underline"
									>
										Delete
									</button>
								</div>
							)}
						</div>
					)}
				</div>
			))}
		</div>
	);
}

function PolicyForm({
	existing,
	onClose,
}: {
	existing?: PolicyRuleSummary;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const [name, setName] = useState(existing?.name ?? '');
	const [description, setDescription] = useState(existing?.description ?? '');
	const [action, setAction] = useState<string>(existing?.action ?? 'observe');
	const [enabled, setEnabled] = useState(existing?.enabled ?? true);

	const createMutation = useMutation({
		mutationFn: (body: unknown) =>
			existing ? put(`/policies/${existing.id}`, body) : post('/policies', body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
			onClose();
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		createMutation.mutate({
			name,
			description: description || undefined,
			action,
			...(existing ? { enabled } : {}),
			conditionConfig: JSON.stringify({
				type: existing?.conditionType ?? 'threat_class',
				threatClass: existing?.conditionType ?? 'T1',
			}),
			scopeType: existing?.scopeType ?? 'all',
		});
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-3">
			<input
				type="text"
				value={name}
				onChange={(e) => setName(e.target.value)}
				placeholder="Policy name"
				className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
				required
			/>
			<input
				type="text"
				value={description}
				onChange={(e) => setDescription(e.target.value)}
				placeholder="Description (optional)"
				className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
			/>
			<select
				value={action}
				onChange={(e) => setAction(e.target.value)}
				className="rounded border border-border bg-background px-3 py-2 text-sm"
			>
				{Object.entries(POLICY_ACTION_LABELS).map(([value, label]) => (
					<option key={value} value={value}>
						{label}
					</option>
				))}
			</select>
			<label className="flex items-center gap-2 text-sm">
				<input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
				Enabled
			</label>
			<div className="flex gap-2">
				<button
					type="submit"
					disabled={!name.trim() || createMutation.isPending}
					className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
				>
					{createMutation.isPending ? 'Saving...' : existing ? 'Update' : 'Create'}
				</button>
				<button
					type="button"
					onClick={onClose}
					className="rounded border border-border px-4 py-2 text-sm"
				>
					Cancel
				</button>
			</div>
		</form>
	);
}

function ExceptionList({ canMutate }: { canMutate: boolean }) {
	const queryClient = useQueryClient();
	const [showNewException, setShowNewException] = useState(false);
	const [reason, setReason] = useState('');

	const { data, isLoading } = useQuery<{ exceptions: PolicyExceptionSummary[] }>({
		queryKey: queryKeys.policies.exceptions(),
		queryFn: () => get<{ exceptions: PolicyExceptionSummary[] }>('/policies/exceptions'),
	});

	const createException = useMutation({
		mutationFn: (body: unknown) => post('/policies/exceptions', body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
			setShowNewException(false);
			setReason('');
		},
	});

	const revokeException = useMutation({
		mutationFn: (id: string) => del(`/policies/exceptions/${id}`),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.policies.all }),
	});

	if (isLoading) {
		return <div className="mt-4 text-muted-foreground">Loading exceptions...</div>;
	}

	const exceptions = data?.exceptions ?? [];

	return (
		<div className="mt-4 space-y-3">
			{canMutate && (
				<button
					type="button"
					onClick={() => setShowNewException(!showNewException)}
					className="text-sm text-primary hover:underline"
				>
					{showNewException ? 'Cancel' : '+ Add Exception'}
				</button>
			)}

			{showNewException && (
				<div className="rounded border border-border p-4">
					<input
						type="text"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Exception reason"
						className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
					/>
					<button
						type="button"
						onClick={() =>
							createException.mutate({
								reason,
								action: 'observe',
								expiresAt: Math.floor(Date.now() / 1000) + 86400 * 30,
							})
						}
						disabled={!reason.trim() || createException.isPending}
						className="mt-2 rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
					>
						Create Exception
					</button>
				</div>
			)}

			{exceptions.length === 0 ? (
				<p className="text-sm text-muted-foreground">No exceptions.</p>
			) : (
				exceptions.map((exc) => (
					<div
						key={exc.id}
						className="flex items-center justify-between rounded border border-border p-3"
					>
						<div className="space-y-0.5">
							<span className="text-sm text-foreground">{exc.reason}</span>
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<span>{exc.isActive ? 'Active' : 'Expired'}</span>
								<span>&middot;</span>
								<span>Expires {formatTimestamp(exc.expiresAt)}</span>
							</div>
						</div>
						{canMutate && exc.isActive && (
							<button
								type="button"
								onClick={() => revokeException.mutate(exc.id)}
								className="text-xs text-destructive hover:underline"
							>
								Revoke
							</button>
						)}
					</div>
				))
			)}
		</div>
	);
}
